import {
  createWalletProviderEventEmitter,
  filterDuplicates,
} from '@dynamic-labs-sdk/client/core';
import type SignClient from '@walletconnect/sign-client';
import type { SessionTypes } from '@walletconnect/types';

import { splitCaip10Token } from '../splitCaip10Token';

type CreateWalletConnectSessionEventEmitterParams = {
  /**
   * Optional function to format addresses. If not provided, addresses are used as-is.
   */
  formatAddress?: (address: string) => string;
  getSession: () => SessionTypes.Struct;
  namespaceKey: string;
  sessionTopic: string;
  signClient: SignClient;
};

type SessionEventPayload = {
  params: {
    event: {
      data: unknown;
      name: string;
    };
  };
  topic: string;
};

/**
 * Creates a WalletConnect session event emitter with common event handling logic.
 *
 * Handles chainChanged, accountsChanged, and disconnected events from WalletConnect sessions.
 *
 * @param params.getSession - Function to get the current session
 * @param params.namespaceKey - The WalletConnect namespace key (e.g., 'eip155', 'solana')
 * @param params.sessionTopic - The session topic to listen for events
 * @param params.signClient - The WalletConnect sign client instance
 * @param params.formatAddress - Optional function to format addresses (e.g., EIP-55 for EVM)
 * @returns An object with cleanupEventEmitter and getEventEmitter functions
 */
export const createWalletConnectSessionEventEmitter = ({
  formatAddress,
  getSession,
  namespaceKey,
  sessionTopic,
  signClient,
}: CreateWalletConnectSessionEventEmitterParams) => {
  return createWalletProviderEventEmitter<(event: SessionEventPayload) => void>(
    {
      removeEventListeners: ({ setupReturnValue: handleSessionEvent }) => {
        signClient.off('session_event', handleSessionEvent);
      },

      setupEventListeners: ({
        handleAccountsChanged,
        handleDisconnected,
        handleNetworkChanged,
      }) => {
        const handleSessionEvent = ({
          params: { event },
          topic,
        }: SessionEventPayload) => {
          if (topic !== sessionTopic) {
            return;
          }

          if (event.name === 'chainChanged') {
            const networkParam = event.data as string;

            // Handle potentially CAIP-2 format
            const networkId = networkParam.startsWith(`${namespaceKey}:`)
              ? networkParam.split(':')[1]
              : networkParam;

            handleNetworkChanged({ networkId });

            return;
          }

          if (event.name === 'accountsChanged') {
            const accountsParam = event.data as string[];

            const addresses = filterDuplicates(
              accountsParam.map((account) => {
                // Handle potentially CAIP-10 format
                if (account.startsWith(`${namespaceKey}:`)) {
                  return splitCaip10Token(account).address;
                }

                return account;
              })
            ).map((address) =>
              formatAddress ? formatAddress(address) : address
            );

            handleAccountsChanged({ addresses });

            return;
          }

          if (event.name === 'disconnected') {
            handleDisconnected();

            return;
          }
        };

        // Assert the session is still valid
        getSession();

        signClient.on('session_event', handleSessionEvent);

        return handleSessionEvent;
      },
    }
  );
};
